import { useState } from 'react';
import { revenueByPeriod, type RevenueSummary } from '@/mocks/finance';

type Period = 'today' | 'week' | 'month';

const periodLabels: Record<Period, string> = {
  today: 'اليوم',
  week: 'الأسبوع',
  month: 'الشهر',
};

function BarChart({ data }: { data: RevenueSummary['chart'] }) {
  const max = Math.max(...data.map((point) => point.value), 1);
  return (
    <div className="mt-6">
      <div className="flex items-end gap-2">
        {data.map((point) => {
          const height = Math.max((point.value / max) * 100, 4);
          return (
            <div key={point.label} className="group flex flex-1 flex-col items-center gap-2">
              <div className="flex h-40 w-full items-end justify-center">
                <div
                  className="relative w-full min-w-[30px] max-w-[60px] rounded-t-md bg-secondary-500/80 transition-all duration-300 hover:bg-secondary-500"
                  style={{ height: `${height}%` }}
                >
                  <span className="absolute -top-7 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-foreground-950 px-2 py-0.5 text-[11px] text-white group-hover:block">
                    {point.value.toLocaleString('ar-SA')} ر.س
                  </span>
                </div>
              </div>
              <span className="text-[10px] text-foreground-400">{point.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function RevenueTab() {
  const [period, setPeriod] = useState<Period>('today');
  const data = revenueByPeriod[period];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold text-[15px] text-foreground-950">ملخص الإيرادات</h3>
        <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
          {(Object.keys(periodLabels) as Period[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={[
                'rounded-full px-4 py-1.5 text-[13px] font-medium whitespace-nowrap transition-colors',
                period === p ? 'bg-primary-500 text-white' : 'text-foreground-500 hover:text-foreground-700',
              ].join(' ')}
            >
              {periodLabels[p]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: 'إيرادات الطلبات المباشرة', value: data.direct, icon: 'ri-shopping-bag-3-line', tone: 'secondary' },
          { label: 'إيرادات الباقات', value: data.packages, icon: 'ri-box-3-line', tone: 'info' },
          { label: 'رسوم التوصيل المحوّلة', value: data.deliveryFees, icon: 'ri-e-bike-2-line', tone: 'accent', note: 'مندوبين تابعين فقط' },
        ].map((stat) => (
          <div
            key={stat.label}
            className="rounded-card border border-background-200 bg-white p-5 shadow-card"
          >
            <div className="flex items-start gap-3">
              <span
                className={[
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]',
                  stat.tone === 'secondary' ? 'bg-secondary-100 text-secondary-600' :
                  stat.tone === 'info' ? 'bg-info-100 text-info-600' :
                  'bg-accent-100 text-accent-600',
                ].join(' ')}
              >
                <i className={`${stat.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] text-foreground-500">{stat.label}</p>
                {stat.note && <p className="text-[11px] text-foreground-400">({stat.note})</p>}
                <p className="mt-1.5 font-bold text-[22px] leading-tight text-foreground-950">
                  {stat.value.toLocaleString('ar-SA')} <span className="text-[14px] font-medium text-foreground-400">ر.س</span>
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-card border-r-4 border-accent-500 border border-background-200 bg-white p-5 shadow-card">
        <p className="text-[13px] text-foreground-500">إجمالي الإيرادات</p>
        <p className="mt-1 font-bold text-[32px] leading-tight text-foreground-950">
          {data.total.toLocaleString('ar-SA')} <span className="text-[18px] font-medium text-foreground-400">ر.س</span>
        </p>

        <BarChart data={data.chart} />
      </div>
    </div>
  );
}